import EmojiPicker from 'emoji-picker-react';
import parse, { domToReact } from "html-react-parser";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { useLazyQuery, useMutation } from "@apollo/client";
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Button, Divider, Grid, IconButton, Popper, Typography, Icon } from "@mui/material";
import {
  ADD_NEWS_COMMENT,
  CREATE_NEWSPOST,
  UPDATE_NEWSPOST_LIKE,
  VOTE_NEWSPOST,
  DELETE_NEWS_POST
} from "../../../graphql/mutations/NewsContext";
import {
  GET_NEWS_FOR_SITE,
  GET_NEWS_FOR_USER,
} from "../../../graphql/queries/NewsContext";
import { getCurrentUser, getSiteRoleRights } from "../../../helpers/selectors";
import Avatar from "../../common/Avatar";
import Mention from "../../common/Mentions/Mention";
import DashboardWidget from "../../widgets/ContainerWidget/DashboardWidget"
import DashboardWidgetBody from "../../widgets/ContainerWidget/DashboardWidgetBody"
import Comments from "./Comments";
import EmoticonDisplay from "./EmoticonDisplay";
import ImageGallery from "./ImageGallery";
import NewsFlowPostModal from "./NewsFlowPostModal";
import PollAnswer from "./PollAnswer";
import PollResult from "./PollResult";
import ReactionButton from "./ReactionButton";
import ImageView from '../../ProductsMainView/ImageView';
import ConfirmationModal from '../../common/ConfirmationModal';
import { ConfirmDeleteModal } from '../../common';
import TipTapMentionEditor from '../../common/Mentions/TipTapMentionEditor';
//import TestImageGallery from "./TestImageGallery";

const NewsFlowWidget = ({
  layout,
  projectId,
  siteId
}) => {
  const user = useSelector(getCurrentUser);
  const siteRoleRights = useSelector(getSiteRoleRights);

  const { canEditNews } = siteRoleRights || {};
  const { newsFilterSetting } = layout || {};

  const [addComment, { loading: addingComment }] = useMutation(ADD_NEWS_COMMENT);
  const [castVote] = useMutation(VOTE_NEWSPOST);
  const [createNewsPost, { loading: savingNewsPost }] = useMutation(CREATE_NEWSPOST);
  const [updateNewsPostLike] = useMutation(UPDATE_NEWSPOST_LIKE)
  const [deleteNewsPost] = useMutation(DELETE_NEWS_POST);

  const [getNewsForSite, { data, error, loading }] = useLazyQuery(GET_NEWS_FOR_SITE);
  const [getNewsForUser, { data: companyNewsData, error: companyNewsError, loading: companyNewsLoading }] = useLazyQuery(GET_NEWS_FOR_USER);

  const commentRefs = useRef({});

  const [activePostId, setActivePostId] = useState(null); // Track which news post is active
  const [commentValues, setCommentValues] = useState({}); // Add this to track values
  const [emoticonAnchorEl, setEmoticonAnchorEl] = useState(null);
  const [modalState, setModalState] = useState({ open: false, content: "", poll: null, imageAttachments: null });
  const [newsMain, setNews] = useState([]);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [showEmoticonPopper, setShowEmoticonPopper] = useState(false);

  const [postToDelete, setPostToDelete] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const hideEmoticonPopper = () => {
    setEmoticonAnchorEl(null);
    setShowEmoticonPopper(false);
  };

  const handleSendMessageButtonClick = (siteNewsId) => {
    debugger;
    const editor = commentRefs.current[siteNewsId];
    if (!editor) return;

    handleAddComment(siteNewsId, editor.getHTML());

    editor.clear();
  };

  const handleEmoticonOpen = (event, siteNewsId) => {
    setActivePostId(siteNewsId); // Set the active post ID
    if (!showEmoticonPopper || emoticonAnchorEl !== event.currentTarget) {
      setEmoticonAnchorEl(event.currentTarget);
      setShowEmoticonPopper(true);
    } else {
      hideEmoticonPopper();
    }
  };

  const handleEmojiClick = (emoji) => {
    if (activePostId) {
      commentRefs.current[activePostId]?.insertEmoticon(emoji.emoji);
    }

    hideEmoticonPopper();
  };

  const isEmptyText = (inputText) => {
    const text = (inputText || "").trim();
    return (text.length === 0 || text === "<p></p>");
  };

  const handleAddComment = async (siteNewsId, commentText) => {
    if (isEmptyText(commentText)) {
      return;
    }

    try {

      const replacedMentions = convertMentionsToText(commentText);

      const response = await addComment({
        variables: {
          siteNewsPostid: siteNewsId,
          comment: replacedMentions
        }
      });

      const newComment = response.data.newsContext.addNewsComment;

      // Clear the input after successful comment
      setCommentValues(prev => ({
        ...prev,
        [siteNewsId]: ''
      }));

      commentRefs.current[siteNewsId]?.clear();

      setNews((prevNews) =>
        prevNews.map((item) =>
          item.id === siteNewsId ?
            { ...item, comments: [...item.comments, newComment] } : item));
    }
    catch (error) {
      console.error('Error adding comment:', error);
    }
  }

  const handleLikeNewsPost = async (siteNewsId, emoticon) => {
    try {
      const { data } = await updateNewsPostLike({
        variables: {
          siteNewsId,
          emoticon,
        },
      });

      const newReaction = data.newsContext.updatePostLikes;

      setNews((prevNews) =>
        prevNews.map((item) =>
          item.id === siteNewsId
            ? {
              ...item,
              likes: newReaction.emoticon === 0
                ? item.likes.filter((like) => like.userId !== newReaction.userId)
                : [
                  ...item.likes.filter((like) => like.userId !== newReaction.userId),
                  newReaction,
                ],
            }
            : item
        )
      );
    } catch (error) {
      console.error("Error updating reaction:", error);
    }
  };

  const confirmDeleteNewsPost = async () => {
    try {
      const res = await deleteNewsPost({
        variables: { siteNewsId: postToDelete },
      });

      if (res.data.newsContext.deleteNewsPost.success) {
        setNews((prevNews) =>
          prevNews.filter((post) => post.id !== postToDelete)
        );
      } else {
        console.error("Failed to delete news post");
      }
    } catch (error) {
      console.error("Error deleting news post:", error);
    } finally {
      setPostToDelete(null); // Close modal
    }
  };

  const handleLoadCompleted = (news) => {
    if (news) {
      setNews((prevNews) => [...prevNews, ...news]);
    }
  };

  const loadMore = () => {
    setPage((prevPage) => prevPage + 10);
  }

  useEffect(() => {
    if (data?.newsContext?.getNewsForSite) {

      handleLoadCompleted(data.newsContext.getNewsForSite)
    }
  }, [data, error, loading])

  useEffect(() => {
    if (companyNewsData?.newsContext?.getNewsForUser) {
      handleLoadCompleted(companyNewsData.newsContext.getNewsForUser)
    }
  }, [companyNewsData, companyNewsError, companyNewsLoading])

  const fetchNews = () => {
    if (Number.isFinite(siteId)) {
      getNewsForSite({
        variables: {
          offset: page,
          pageSize: perPage,
          siteId,
          projectId: (newsFilterSetting || 0) == 0 ? null : projectId
        }
      });
    } else {
      getNewsForUser({
        variables: {
          offset: page,
          pageSize: perPage,
          projectId: (newsFilterSetting || 0) == 0 ? null : projectId
        }
      });
    }
  };

  useEffect(() => {
    fetchNews();
  }, [siteId, projectId, page]);

  const handleVoteChoice = (siteNewsId, choices) => {
    setNews((prevNews) =>
      prevNews.map((item) => {
        if (item.id === siteNewsId) {
          return {
            ...item,
            poll: {
              ...item.poll,
              choices: choices,
            },
          };
        }
        return item;
      })
    );
  };

  const handleVote = async (siteNewsId, choices) => {
    const selectedChoiceIds = choices
      .filter(choice => choice.selected)
      .map(choice => choice.id);

    try {
      const response = await castVote({
        variables: {
          input: {
            siteNewsId: siteNewsId,
            choices: selectedChoiceIds
          }
        }
      });

      const updatedPoll = response.data.newsContext.vote;

      // Update the news collection with the new poll data
      setNews((prevNews) =>
        prevNews.map((item) =>
          item.id === siteNewsId
            ? { ...item, poll: updatedPoll }
            : item
        )
      );
    } catch (error) {
      console.error("Error casting vote:", error);
    }
  };

  const convertMentionsToText = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Replace each mention span with {{mention:ID}}
    doc.querySelectorAll("span.mention").forEach((span) => {
      const userId = span.dataset.id;
      const token = `{{mention:${userId}}}`;
      span.replaceWith(token);
    });

    // Return serialized HTML (with p tags preserved)
    return Array.from(doc.body.childNodes)
      .map((node) => node.outerHTML || node.textContent)
      .join("");
  };

  const handleSaveNewsPost = async () => {
    try {

      const processedContent = convertMentionsToText(modalState.content);

      debugger;

      const input = {
        //postImage: uploadedAsset ? uploadedAsset : null,
        thumbnailImage: null,//uploadedAsset,
        allowComments: modalState.allowComments || false,
        category: "",
        leadText: processedContent,
        siteId,
        templateId: null,//selectedTemplateId,
        title: "",
        newsPost: "",
        projectId,
        poll: modalState.poll,
        imageAttachments: modalState.imageAttachments,
        products: modalState.products || [],
        sendEmailToSiteUsers: false
      };

      const res = await createNewsPost({ variables: { input } });

      if (
        res &&
        res.data &&
        res.data.newsContext &&
        res.data.newsContext.createNewsPost
      ) {
        const { success, result } = res.data.newsContext.createNewsPost;
        if (success) {
          let temp = [result, ...newsMain];
          setNews(temp);
          setModalState({ open: false, content: "", poll: null, imageAttachments: null });
        } else {
          /*snackbar.workspaceError(
            <FormattedMessage id="common.genericErrorMessage" />
          );*/
        }
      }
    } catch (err) {
      console.error("error >", err);
    }
  };

  const renderLeadTextWithMentions = (leadText, mentions) => {
    const mentionRegex = /<@m(\d+)>/g;

    const replaceMentions = (node) => {
      if (node.type === "text") {
        const text = node.data;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = mentionRegex.exec(text)) !== null) {
          const mentionId = parseInt(match[1], 10);
          const mentionData = mentions.find((m) => m.mentionId === mentionId);

          // Add preceding text
          if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
          }

          // Add Mention component or fallback text
          if (mentionData) {
            parts.push(
              <Mention

                key={mentionId}
                mentionId={mentionId}
                mentions={mentionData}
              />
            );
          } else {
            parts.push(match[0]); // Raw text if no matching mention
          }

          lastIndex = mentionRegex.lastIndex;
        }

        // Add remaining text
        if (lastIndex < text.length) {
          parts.push(text.substring(lastIndex));
        }

        // Wrap in a single parent element
        return <>{parts}</>;
      }

      // Recursively process child nodes for nested HTML
      if (node.children) {
        return {
          ...node,
          children: domToReact(node.children, { replace: replaceMentions }),
        };
      }

      return node;
    };

    return parse(leadText, { replace: replaceMentions });
  };

  const getSuggestionList = (post) => {
    const suggestionMap = new Map();

    // Add the post author to the suggestions

    suggestionMap.set(post.authorId, {
      name: post.author,
      avatar: post.author.avatarUrl,
      userId: post.authorId,
    });

    // Add the users who commented on the post
    (post.comments || []).forEach(({ author, authorId, avatarUrl }) => {
      suggestionMap.set(authorId, {
        name: author,
        avatar: avatarUrl,
        userId: authorId,
      });
    });

    //Add unique mentions from the post
    (post.mentions || []).forEach(({ userId, name, avatarUrl }) => {
      suggestionMap.set(userId, {
        name: name,
        avatar: avatarUrl,
        userId: userId,
      });
    });

    // Add unique mentions from the comments
    (post.comments || []).forEach(comment => {
      (comment.mentions || []).forEach(({ userId, name, avatarUrl }) => {
        suggestionMap.set(userId, {
          name: name,
          avatar: avatarUrl,
          userId: userId,
        });
      });
    });

    // Convert map values into an array and return
    return Array.from(suggestionMap.values());
  };

  return (
    <DashboardWidget>
      <DashboardWidgetBody>

        <Box
          sx={{
            margin: "auto",
            maxWidth: "500px",
          }}
        >
          {(canEditNews && (siteId || projectId)) && (
            <Box
              sx={{
                alignItems: "center",
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                borderRadius: 2,
                boxShadow: 1,
                display: "flex",
                marginBottom: 3,
                padding: 1,
              }}
            >
              {user && (
                <Grid container>
                  <Grid item xs={2}><Avatar url={user.avatar} author={user.firstname + " " + user.lastname} /></Grid>
                  <Grid item xs={10}>{user.firstname} {user.lastname}</Grid>
                  <Grid item xs={12}><Box sx={{ backgroundColor: "#ddd", borderRadius: 2, padding: 2, marginTop: 1 }} onClick={() => setModalState({ ...modalState, open: true })}>Skriv</Box></Grid>
                </Grid>
              )}
            </Box>
          )}

          {newsMain && newsMain.map((n, index) => {
            const suggestionList = getSuggestionList(n);

            return (
              <Box key={n.id + "_" + index} sx={{ maxWidth: "500px", margin: "auto", padding: 2, borderRadius: 2, border: "1px solid #ddd", backgroundColor: "#fff", marginBottom: 2 }}>

                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Avatar author={n.author} url={n.avatarUrl} />
                  <Box>
                    <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#333" }}>{n.author} {!siteId && (n.site)}</Typography>

                    <Typography sx={{
                      fontSize: "13px", lineHeight: 1.2, fontWeight: 400, color: "#999"
                    }}>
                      {moment(n.createdDate)
                        .format("L")
                        .toString()}
                    </Typography></Box>
                </Box>

                {n.thumbnail && (
                  <Box sx={{
                    width: "100%",
                    marginTop: 2,
                    borderRadius: 1,
                    overflow: "hidden",
                    height: "300px",
                    backgroundColor: "#f0f2f5",
                    backgroundImage: `url(${n.thumbnail})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    lineHeight: "300px",
                    fontSize: 32,
                    textAlign: "center"
                  }}>
                    {n.title}
                  </Box>
                )}

                {!n.thumbnail && (
                  <Typography>{n.title}</Typography>
                )}

                {n.leadText && (
                  <Box
                    sx={{
                      marginTop: 1.5,
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: "#333",
                      whiteSpace: "break-spaces",
                      "p": {
                        marginBottom: 1,
                        marginTop: 1,
                      },
                      "p:first-of-type": {
                        marginTop: 0,
                      },
                      "p:last-of-type": {
                        marginBottom: 0,
                      },
                    }}
                  >
                    {renderLeadTextWithMentions(n.leadText, n.mentions)}
                  </Box>
                )}

                <Box sx={{ marginTop: 1 }}>
                  <ImageGallery
                    imageUrls={(n.images || []).map(x => x.imageUrl)}
                  //onClick={(url) => { }}
                  />

                  {/*{(index === 0) && <TestImageGallery />}*/}
                </Box>

                <Box sx={{ marginTop: 1 }}>
                  {n.products && n.products.length > 0 && (<ImageView products={{ items: n.products }} siteRoleRights={{ imageView: true }} numCols={3} />)}
                </Box>

                {n.poll && !n.poll.hasVoted && (
                  <PollAnswer
                    siteNewsId={n.id}
                    poll={n.poll}
                    onUpdateVote={handleVoteChoice}
                    onVote={handleVote}
                  />
                )}

                {n.poll && n.poll.hasVoted && (
                  <PollResult
                    siteNewsId={n.id}
                    poll={n.poll}
                  />
                )}

                <Divider sx={{ marginTop: 1, marginBottom: 1 }} />

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {/* Left side: EmoticonDisplay + ReactionButton */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmoticonDisplay likes={n.likes} />
                    <ReactionButton postId={n.id} onLikePost={handleLikeNewsPost} />
                  </Box>

                  {/* Spacer pushes Delete button to right */}
                  <Box sx={{ marginLeft: 'auto' }}>
                    {canEditNews && (
                      <IconButton
                        onClick={() => setPostToDelete(n.id)}
                        aria-label="delete news post"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>


                {n.allowComments && (<>
                  <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
                  <Comments
                    comments={n.comments}
                    onAddComments={() => { console.log("comment added"); }}
                    setNews={setNews}
                  />

                  <Box sx={{ position: "relative" }}>
                    <TipTapMentionEditor
                      ref={(editor) => {
                        if (editor) commentRefs.current[n.id] = editor;
                      }}
                      defaultValue={commentValues[n.id] || ''} // use defaultValue once
                      //onChange={(newContent) => handleCommentChange(n.id, newContent)}
                      onEnterPressed={(commentText) => handleAddComment(n.id, commentText)}
                      placeholder="Skriv en kommentar..."
                      minHeight={50}
                      disableMentions={false}
                      disableToolbar={true}  // chat mode, simple editor
                    />

                    {/* Emoji button */}
                    <IconButton
                      size="small"
                      disabled={addingComment}
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 40, // a bit left of the send button
                        color: "primary.main",
                        "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
                      }}
                      onClick={(e) => handleEmoticonOpen(e, n.id)}
                    >
                      <InsertEmoticonIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      disabled={addingComment}
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        color: "primary.main",
                        "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
                      }}
                      title={<FormattedMessage id="messaging.sendMessageButton.title" />}
                      onClick={(e) => handleSendMessageButtonClick(n.id)}
                    >
                      <Icon className="fas fa-paper-plane" fontSize="small" />
                    </IconButton>

                  </Box></>)}

              </Box>
            );
          })}

          <Button onClick={loadMore}>Visa mer</Button>
        </Box>

        <ConfirmDeleteModal
          open={Boolean(postToDelete)}
          onClose={() => setPostToDelete(null)}
          setOpen={() => setPostToDelete(null)}
          onConfirmCloseModal={confirmDeleteNewsPost}
        />

        <NewsFlowPostModal
          isSaving={savingNewsPost}
          modalState={modalState}
          onSave={handleSaveNewsPost}
          setModalState={setModalState}
          user={user}
        />


        <Popper
          anchorEl={emoticonAnchorEl}
          open={showEmoticonPopper}
          placement="bottom-start"
          style={{ position: 'absolute', zIndex: 1300 }}
        >
          <EmojiPicker
            emojiStyle="native"
            emojiVersion="4.0" height={300} width={300}
            onEmojiClick={(emoji) => handleEmojiClick(emoji)}
            previewConfig={{ showPreview: false }}
            searchDisabled={true}
            style={{
              '--epr-emoji-size': '20px',  // This should apply the size
              '--epr-emoji-gap': '4px',    // This controls the gap between emojis
            }}
          />
        </Popper>

      </DashboardWidgetBody>
    </DashboardWidget>
  );
}

export default NewsFlowWidget;
